import { createHead } from '@unhead/vue/client';
import { createPinia } from 'pinia';
import { ConfirmationService, Tooltip } from 'primevue';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { createApp, h } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { routes } from 'vue-router/auto-routes';

import App from './App.vue';

import { clearTokens, hasValidTokens } from '@/api/auth';
import { getUser } from '@/api/user';
import FullscreenSpinner from '@/components/FullscreenSpinner.vue';
import { i18n } from '@/i18n';
import { useAuthStore } from '@/stores/auth';
import '@/styles/main.scss';
import { canAccessAdmin } from '@/utils/adminAccess';
//@maxloo2 I don't see ENV_MODE used in the code, please check this
const ENV_MODE = import.meta.env.MODE;

// Boot overlay using the Vue spinner component
const bootHost = document.createElement('div');
bootHost.id = 'stw-boot-host';
document.body.appendChild(bootHost);
const bootApp = createApp({ render: () => h(FullscreenSpinner) });
bootApp.mount('#stw-boot-host');

const app = createApp(App);

// Dev-only: emits performance.mark/measure per component init/compile/render/patch,
// consumed by useVueRenderTiming.
// Vue compiles this instrumentation out of production bundles entirely, so this
// flag is a no-op there regardless - the DEV guard here is just for clarity.
if (import.meta.env.DEV) {
  app.config.performance = true;
}

const pinia = createPinia();

const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...routes,
    // The EULA covers privacy too (2026-10-14), so the old page points at
    // its "Information we collect" section.
    { path: '/privacy-policy', redirect: '/eula#information' },
  ],
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash };
    return { top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  // Admin route protection — this panel is platform administration (user
  // list, seed-data migration, dev tooling), not instructor-facing, so it's
  // gated on is_admin specifically, not the broader Faculty/PROF role (a
  // future instructor dashboard would be a separate, PROF-facing route).
  // The local dev server skips the check (see canAccessAdmin).
  if (to.path.startsWith('/admin')) {
    const authStore = useAuthStore();
    const user = authStore.user;

    // If auth hasn't initialized yet, allow navigation to proceed —
    // the page itself re-checks once auth settles.
    if (!authStore.isInitialized) {
      next();
      return;
    }

    if (!canAccessAdmin(user)) {
      next('/');
      return;
    }
  }

  if (to.matched.length === 0) {
    next('/');
  } else {
    next();
  }
});

app.use(router);

app.use(pinia);
app.use(i18n);

app.use(PrimeVue, {
  theme: 'none',
});
app.use(ToastService);
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);

const head = createHead();
app.use(head);

// Main initialization
(async () => {
  const authStore = useAuthStore();
  let isAppMounted = false;

  const mountApp = () => {
    if (isAppMounted) return;
    app.mount('#app');
    isAppMounted = true;
    bootApp.unmount();
    bootHost.remove();
  };

  try {
    // Check if user has valid JWT tokens and fetch user data if so
    if (hasValidTokens()) {
      try {
        const user = await getUser();
        await authStore.setUser(user);
      } catch (error) {
        console.warn(
          '[main.ts] Failed to fetch user using stored JWT tokens',
          error
        );
        // Clear invalid tokens
        clearTokens();
      }
    }

    // Mark auth as initialized
    authStore.setInitialized();

    // Always mount the app
    mountApp();
  } catch (error) {
    console.warn('[main.ts] App initialization error:', error);
    // Mark auth as initialized even if it fails
    authStore.setInitialized();
    // Still mount app even if auth fails
    mountApp();
  }
})();
